import { useCallback, useMemo, useState } from 'react';
import { format, isValid } from 'date-fns';

import { Box, Button, ButtonGroup, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';

import { useRentalManagerUserPropertySpecialOfferV2WebApiHandler_delete } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';

import SpecialOffersModal from '../../modals/SpecialOffersModal';

import type { GetUserPropertyDetailsSpecialOfferWebDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  isInPostingPath?: boolean;
  isFeed: boolean;
  listingAlias: string;
  specialOffer: GetUserPropertyDetailsSpecialOfferWebDto;
};

const SpecialOfferItem = ({ isInPostingPath = false, isFeed, listingAlias, specialOffer }: Props) => {
  const { startDateYyyyMmDd: startDate, endDateYyyyMmDd: endDate, description, offerIdEncoded } = specialOffer;

  const { enqueueToast } = useToast();
  const [showEditModal, setShowEditModal] = useState(false);

  const { mutate: deleteOffer, isPending: isDeleting } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertySpecialOfferV2WebApiHandler_delete,
    meta: {
      context: {
        api: 'RentalManagerUserPropertySpecialOfferV2WebApiHandler_delete',
        details: { listingAlias, offerIdEncoded },
      },
    },
    onError: () => {
      enqueueToast(<Toast tone="critical">Offer couldn’t be deleted. Refresh and try again.</Toast>);
    },
  });

  const handleRemoveOfferClick = useCallback(
    () =>
      deleteOffer({
        postData: {
          listingAlias,
          offerIdEncoded: offerIdEncoded || '',
        },
      }),
    [deleteOffer, listingAlias, offerIdEncoded],
  );

  const formattedStartDate = useMemo(
    () =>
      isValid(new Date(`${startDate}T10:00:00`)) ? format(new Date(`${startDate}T10:00:00`), 'MMM dd, yyyy') : ' - ',
    [startDate],
  );
  const formattedEndDate = useMemo(
    () => (isValid(new Date(`${endDate}T10:00:00`)) ? format(new Date(`${endDate}T10:00:00`), 'MMM dd, yyyy') : ' - '),
    [endDate],
  );

  const offerDateDisplayText = useMemo(() => {
    switch (true) {
      case Boolean(!startDate && !endDate):
        return 'Ongoing';
      case Boolean(!startDate && endDate):
        return `Until ${formattedEndDate}`;
      case Boolean(startDate && !endDate):
        return `${formattedStartDate}-ongoing`;
      default:
        return `${formattedStartDate}–${formattedEndDate}`;
    }
  }, [startDate, endDate, formattedStartDate, formattedEndDate]);

  return (
    <div data-testid="SpecialOfferDisplay">
      <Box css={{ marginTop: '200', marginBottom: isInPostingPath ? '800' : '200' }}>
        <Paragraph textStyle={isInPostingPath ? 'body-bold' : 'body-sm-bold'} css={{ marginBottom: '200' }}>
          {offerDateDisplayText}
        </Paragraph>
        <Paragraph css={{ wordBreak: 'break-word', whiteSpace: 'break-spaces' }}>{description}</Paragraph>
      </Box>
      <ButtonGroup css={{ display: 'flex' }} aria-label="Special offer actions">
        {isInPostingPath && (
          <Button
            onClick={() => setShowEditModal(true)}
            tone="neutral"
            emphasis="outlined"
            size="md"
            css={{ marginRight: '800' }}
          >
            Edit offer details
          </Button>
        )}

        {!isFeed && (
          <TextButton
            icon={<IconDeleteOutline />}
            onClick={handleRemoveOfferClick}
            disabled={isDeleting}
            tone={isInPostingPath ? 'neutral' : 'brand'}
            css={{ paddingInline: '0' }}
          >
            Remove offer
          </TextButton>
        )}
      </ButtonGroup>

      {showEditModal && (
        <SpecialOffersModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          defaultOffer={specialOffer}
        />
      )}
    </div>
  );
};

export default SpecialOfferItem;
